import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ActiveTool } from '../enums/ActiveTool';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { SheetCalibrationButton } from 'src/components/common/forgeViewerInteractions/SheetCalibrationButton';
import { useProtanProjectSteps } from 'src/components/common/ProtanProjectStepsProvider';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import {
  CalibrationUnit,
  CreateCalibrationDocument,
  EventResource,
  EventType,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
  UpdateCalibrationDocument,
} from 'src/gql/graphql';
import { useEventHistory } from 'src/hooks/event-history';
import { useUserTenant } from 'src/services/auth-info';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';

// Standard paper sizes in PDF points and their physical dimensions in millimeters
const PAPER_SIZES = [
  // A series
  {
    name: 'A0',
    widthPt: 2383.937,
    heightPt: 3370.394,
    widthMm: 841,
    heightMm: 1189,
  },
  {
    name: 'A1',
    widthPt: 1683.78,
    heightPt: 2383.937,
    widthMm: 594,
    heightMm: 841,
  },
  {
    name: 'A2',
    widthPt: 1190.551,
    heightPt: 1683.78,
    widthMm: 420,
    heightMm: 594,
  },
  {
    name: 'A3',
    widthPt: 841.89,
    heightPt: 1190.551,
    widthMm: 297,
    heightMm: 420,
  },
  {
    name: 'A4',
    widthPt: 595.276,
    heightPt: 841.89,
    widthMm: 210,
    heightMm: 297,
  },
  // US Letter
  {
    name: 'Letter',
    widthPt: 612,
    heightPt: 792,
    widthMm: 215.9,
    heightMm: 279.4,
  },
  // US Legal
  {
    name: 'Legal',
    widthPt: 612,
    heightPt: 1008,
    widthMm: 215.9,
    heightMm: 355.6,
  },
] as const;

const TOLERANCE = 1; // Points tolerance for matching paper sizes

/**
 * Detect paper size from PDF dimensions and return physical dimensions
 */
function detectPaperSize(
  widthPt: number,
  heightPt: number
): { widthMm: number; heightMm: number; name: string } | null {
  // Try to match both orientations
  for (const paper of PAPER_SIZES) {
    // Portrait orientation
    if (
      Math.abs(widthPt - paper.widthPt) < TOLERANCE &&
      Math.abs(heightPt - paper.heightPt) < TOLERANCE
    ) {
      return {
        widthMm: paper.widthMm,
        heightMm: paper.heightMm,
        name: paper.name,
      };
    }
    // Landscape orientation
    if (
      Math.abs(widthPt - paper.heightPt) < TOLERANCE &&
      Math.abs(heightPt - paper.widthPt) < TOLERANCE
    ) {
      return {
        widthMm: paper.heightMm,
        heightMm: paper.widthMm,
        name: paper.name,
      };
    }
  }
  return null;
}

/**
 * Calculate calibration points using page edges
 */
function calculateCalibrationPoints(
  useWidth: boolean,
  pageWidthPt: number,
  pageHeightPt: number
): { pointA: { x: number; y: number }; pointB: { x: number; y: number } } {
  if (useWidth) {
    // Horizontal calibration: use top edge
    return {
      pointA: { x: 0, y: 0 },
      pointB: { x: pageWidthPt, y: 0 },
    };
  }
  // Vertical calibration: use left edge
  return {
    pointA: { x: 0, y: 0 },
    pointB: { x: 0, y: pageHeightPt },
  };
}

/**
 * Get physical dimension in millimeters from PDF dimensions
 */
function getPhysicalDimensionMm(
  pageWidthPt: number,
  pageHeightPt: number,
  useWidth: boolean
): number {
  const paperSize = detectPaperSize(pageWidthPt, pageHeightPt);
  const dimensionPt = useWidth ? pageWidthPt : pageHeightPt;

  if (paperSize) {
    return useWidth ? paperSize.widthMm : paperSize.heightMm;
  }

  // Fallback: convert PDF points to millimeters
  // 1 point = 1/72 inch = 25.4/72 mm
  const POINTS_TO_MM = 25.4 / 72;
  return dimensionPt * POINTS_TO_MM;
}

/**
 * Detect if calibration was created using numeric method
 * Numeric calibrations have points at page edges/corners (no one would measure from there visually)
 */
function isNumericCalibration(
  pointA: { x: number; y: number },
  pointB: { x: number; y: number },
  pageWidthPt: number,
  pageHeightPt: number
): boolean {
  const EDGE_TOLERANCE = 2; // Points tolerance for edge detection

  // Check if points are at corners
  const corners = [
    { x: 0, y: 0 },
    { x: pageWidthPt, y: 0 },
    { x: 0, y: pageHeightPt },
    { x: pageWidthPt, y: pageHeightPt },
  ];

  const isPointAtCorner = (point: { x: number; y: number }) =>
    corners.some(
      (corner) =>
        Math.abs(point.x - corner.x) < EDGE_TOLERANCE &&
        Math.abs(point.y - corner.y) < EDGE_TOLERANCE
    );

  // Check if points are on edges
  const isPointOnEdge = (point: { x: number; y: number }) =>
    Math.abs(point.x) < EDGE_TOLERANCE ||
    Math.abs(point.x - pageWidthPt) < EDGE_TOLERANCE ||
    Math.abs(point.y) < EDGE_TOLERANCE ||
    Math.abs(point.y - pageHeightPt) < EDGE_TOLERANCE;

  return (
    isPointAtCorner(pointA) ||
    isPointAtCorner(pointB) ||
    (isPointOnEdge(pointA) && isPointOnEdge(pointB))
  );
}

/**
 * Calculate scale ratio from existing calibration
 */
function calculateScaleRatioFromCalibration(
  calibration: {
    pointA: { x: number; y: number };
    pointB: { x: number; y: number };
    destinationWidth: number;
    destinationUnit: CalibrationUnit;
  },
  pageWidthPt: number,
  pageHeightPt: number
): number | null {
  // Calculate distance between points in API coordinates (PDF points)
  const apiDistance = Math.sqrt(
    Math.pow(calibration.pointB.x - calibration.pointA.x, 2) +
      Math.pow(calibration.pointB.y - calibration.pointA.y, 2)
  );

  if (apiDistance === 0) {
    return null;
  }

  // Determine which dimension was used
  const useWidth =
    Math.abs(calibration.pointB.x - calibration.pointA.x) >=
    Math.abs(calibration.pointB.y - calibration.pointA.y);

  // Get physical dimension in millimeters
  const physicalDimensionMm = getPhysicalDimensionMm(
    pageWidthPt,
    pageHeightPt,
    useWidth
  );

  // Convert destinationWidth to meters
  let destinationWidthM: number;
  switch (calibration.destinationUnit) {
    case CalibrationUnit.Mm:
      destinationWidthM = calibration.destinationWidth / 1000;
      break;
    case CalibrationUnit.Feet:
      destinationWidthM = calibration.destinationWidth * 0.3048;
      break;
    case CalibrationUnit.M:
    default:
      destinationWidthM = calibration.destinationWidth;
      break;
  }

  // Calculate scale ratio: scaleRatio = destinationWidthM / physicalDimensionM
  const physicalDimensionM = physicalDimensionMm / 1000;
  const scaleRatio = destinationWidthM / physicalDimensionM;

  return scaleRatio;
}

export function SheetCalibration() {
  const { t } = useTranslation('project');
  const {
    calibration,
    setActiveTool,
    activeTool,
    drawing: { setDrawingMode },
    sheets,
    activeSheet,
    pagination,
    viewer,
  } = useSheetViewerV2();
  const { isCalibrating, isComplete, isUpdating, enable, disable } =
    calibration.tool;
  const [tabIndex, setTabIndex] = useState(0);
  const { register, handleSubmit, reset } = useForm<{
    length: number;
    units: CalibrationUnit;
  }>();
  const {
    register: registerNumeric,
    handleSubmit: handleSubmitNumeric,
    reset: resetNumeric,
  } = useForm<{
    scaleRatio: number;
  }>();

  const { isProtan } = useUserTenant();
  const { setStepStatus, projectSteps } = useProtanProjectSteps();
  const wasAutoEnabled = useRef(false);
  const suppressAutoEnableRef = useRef(false);
  const { logEvent } = useEventHistory(activeSheet?.data?.projectId ?? '');

  const [createCalibrationResults, createCalibration] = useMutation(
    CreateCalibrationDocument
  );
  const [updateCalibrationResults, updateCalibration] = useMutation(
    UpdateCalibrationDocument
  );

  const toggleCalibration = useCallback(() => {
    if (calibration.existing && activeSheet?.data) {
      const pageWidthPt = activeSheet.data.width || 595.276;
      const pageHeightPt = activeSheet.data.height || 841.89;

      // Detect if this is a numeric calibration
      const isNumeric = isNumericCalibration(
        calibration.existing.pointA,
        calibration.existing.pointB,
        pageWidthPt,
        pageHeightPt
      );

      if (isNumeric) {
        // Calculate scale ratio from existing calibration
        const scaleRatio = calculateScaleRatioFromCalibration(
          calibration.existing,
          pageWidthPt,
          pageHeightPt
        );

        if (scaleRatio !== null) {
          resetNumeric({
            scaleRatio: Math.round(scaleRatio),
          });
          setTabIndex(1); // Switch to numeric tab
        } else {
          // Fallback to visual tab if calculation fails
          reset({
            length: calibration.existing.destinationWidth,
            units: calibration.existing.destinationUnit,
          });
          setTabIndex(0);
        }
      } else {
        // Visual calibration
        reset({
          length: calibration.existing.destinationWidth,
          units: calibration.existing.destinationUnit,
        });
        setTabIndex(0); // Switch to visual tab
      }
    }

    // Mark as manually toggled (not auto-enabled)
    wasAutoEnabled.current = false;
    return !isCalibrating ? enable() : disable();
  }, [
    calibration,
    activeSheet?.data,
    isCalibrating,
    enable,
    disable,
    reset,
    resetNumeric,
  ]);

  const drawingCalibrationStep = projectSteps?.find(
    (step) => step.step === ProtanProjectStepEnum.DrawingCalibration
  );

  const shouldShowCalibration = useMemo(
    () =>
      isProtan &&
      !calibration.existing &&
      drawingCalibrationStep?.status === ProtanProjectStepStatusEnum.NotStarted,
    [isProtan, calibration.existing, drawingCalibrationStep]
  );

  useEffect(() => {
    // Only auto-enable once per mount when conditions are stable; never auto-disable here
    if (
      shouldShowCalibration &&
      !wasAutoEnabled.current &&
      !isCalibrating &&
      activeTool === ActiveTool.Default &&
      !suppressAutoEnableRef.current
    ) {
      wasAutoEnabled.current = true;
      enable();
    }
  }, [shouldShowCalibration, isCalibrating, activeTool, enable]);

  // Reset suppression once initial auto-enable window ends
  useEffect(() => {
    if (!shouldShowCalibration) {
      suppressAutoEnableRef.current = false;
    }
  }, [shouldShowCalibration]);

  // Auto-detect numeric calibration when calibration exists and popover opens
  useEffect(() => {
    if (
      isCalibrating &&
      calibration.existing &&
      activeSheet?.data &&
      !isComplete
    ) {
      const pageWidthPt = activeSheet.data.width || 595.276;
      const pageHeightPt = activeSheet.data.height || 841.89;

      const isNumeric = isNumericCalibration(
        calibration.existing.pointA,
        calibration.existing.pointB,
        pageWidthPt,
        pageHeightPt
      );

      if (isNumeric) {
        const scaleRatio = calculateScaleRatioFromCalibration(
          calibration.existing,
          pageWidthPt,
          pageHeightPt
        );

        if (scaleRatio !== null) {
          resetNumeric({
            scaleRatio: Math.round(scaleRatio),
          });
          setTabIndex(1); // Switch to numeric tab
        }
      } else {
        reset({
          length: calibration.existing.destinationWidth,
          units: calibration.existing.destinationUnit,
        });
        setTabIndex(0); // Switch to visual tab
      }
    }
  }, [
    isCalibrating,
    calibration.existing,
    activeSheet?.data,
    isComplete,
    reset,
    resetNumeric,
  ]);

  const onSubmit = handleSubmit(async ({ length, units }) => {
    // Prevent auto-enable from re-triggering while we transition away from calibration
    suppressAutoEnableRef.current = true;

    // For Protan, mark steps first to avoid auto re-enable, then jump into drawing
    if (isProtan) {
      wasAutoEnabled.current = false;
      if (shouldShowCalibration) {
        Object.values(ProtanProjectStepEnum).forEach((step) => {
          setStepStatus(step, ProtanProjectStepStatusEnum.Completed);
        });
      }
      setActiveTool(ActiveTool.Drawing);
      setDrawingMode(DrawingMode.MagicPolygon);
    } else {
      // Non-Protan: simply exit calibration
      disable();
    }

    try {
      await calibration.createOrUpdate(length, units);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[SheetCalibration] createOrUpdate failed', error);
    }

    if (shouldShowCalibration && !isProtan) {
      // Set all steps to completed for now
      Object.values(ProtanProjectStepEnum).forEach((step) => {
        setStepStatus(step, ProtanProjectStepStatusEnum.Completed);
      });
    }
  });

  const createNumericCalibration = useCallback(
    async (scaleRatio: number) => {
      if (!activeSheet?.data || !viewer.aggregated?.viewer) {
        return;
      }

      // Get page dimensions (PDF points)
      const pageWidthPt = activeSheet.data.width || 595.276;
      const pageHeightPt = activeSheet.data.height || 841.89;

      // Determine which dimension to use (width or height)
      // Use the larger dimension for calibration
      const useWidth = pageWidthPt >= pageHeightPt;

      // Get physical dimension in millimeters
      const physicalDimensionMm = getPhysicalDimensionMm(
        pageWidthPt,
        pageHeightPt,
        useWidth
      );

      // Calculate calibration points in PDF coordinates: use edges instead of center
      const { pointA: pdfPointA, pointB: pdfPointB } =
        calculateCalibrationPoints(useWidth, pageWidthPt, pageHeightPt);

      // Convert PDF points to API coordinates
      // API coordinates are PDF coordinates stored directly (they match PDF.js coordinate system)
      // So we can use PDF points directly as API coordinates
      const apiPointA = { x: pdfPointA.x, y: pdfPointA.y };
      const apiPointB = { x: pdfPointB.x, y: pdfPointB.y };

      // Scale calculation: 1:100 means "1 unit on paper = 100 units in reality"
      // getUnitScaleFromCalibration calculates: scale = destinationWidth / apiDistance
      // where apiDistance is the distance between pointA and pointB in API coordinates (PDF points)
      // We want scale = scaleRatio, so: destinationWidth = scaleRatio * apiDistance
      //
      // The distance between pointA and pointB in API coordinates represents the physical paper dimension.
      // So if physicalDimensionMm is the physical dimension in mm, then:
      // realWorldDistance = physicalDimensionMm * scaleRatio / 1000 (convert mm to meters)
      // For numeric calibration, we always store in meters since the scale ratio is unit-independent
      const physicalDimensionM = physicalDimensionMm / 1000;
      const destinationWidth = physicalDimensionM * scaleRatio;

      const calibrationData = {
        destinationUnit: CalibrationUnit.M, // Always use meters for numeric calibration
        destinationWidth,
        pointA: apiPointA,
        pointB: apiPointB,
      };

      if (!calibration.existing) {
        await createCalibration({
          input: {
            sheetPageCalibration: {
              sheetId: activeSheet.data.id,
              pageNumber: pagination.pageNumber,
              calibration: calibrationData,
            },
          },
        });
      } else {
        await updateCalibration({
          input: {
            sheetId: activeSheet.data.id,
            pageNumber: pagination.pageNumber,
            patch: { calibration: calibrationData },
          },
        });
      }

      logEvent({
        eventName: 'Calibration Updated',
        eventType: EventType.Modified,
        resourceName: 'Calibration',
        resourceType: EventResource.Sheet,
        metadata: {
          affectedResources: [activeSheet.data.id],
        },
      });
    },
    [
      activeSheet?.data,
      viewer.aggregated?.viewer,
      calibration.existing,
      createCalibration,
      logEvent,
      pagination.pageNumber,
      updateCalibration,
    ]
  );

  const isNumericUpdating = useMemo(
    () =>
      createCalibrationResults.fetching ||
      updateCalibrationResults.fetching ||
      false,
    [createCalibrationResults.fetching, updateCalibrationResults.fetching]
  );

  const onSubmitNumeric = handleSubmitNumeric(async ({ scaleRatio }) => {
    // Prevent auto-enable from re-triggering while we transition away from calibration
    suppressAutoEnableRef.current = true;

    // For Protan, mark steps first to avoid auto re-enable, then jump into drawing
    if (isProtan) {
      wasAutoEnabled.current = false;
      if (shouldShowCalibration) {
        Object.values(ProtanProjectStepEnum).forEach((step) => {
          setStepStatus(step, ProtanProjectStepStatusEnum.Completed);
        });
      }
      setActiveTool(ActiveTool.Drawing);
      setDrawingMode(DrawingMode.MagicPolygon);
    } else {
      // Non-Protan: simply exit calibration
      disable();
    }

    try {
      await createNumericCalibration(scaleRatio);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[SheetCalibration] createNumericCalibration failed', error);
    }

    if (shouldShowCalibration && !isProtan) {
      // Set all steps to completed for now
      Object.values(ProtanProjectStepEnum).forEach((step) => {
        setStepStatus(step, ProtanProjectStepStatusEnum.Completed);
      });
    }
  });

  // Hide button if no sheets are loaded
  if (!sheets || sheets.length === 0) {
    return null;
  }

  return (
    <Popover isOpen={isCalibrating} closeOnBlur={false} isLazy>
      <PopoverAnchor>
        <SheetCalibrationButton
          hasCalibration={!!calibration.existing}
          onClick={toggleCalibration}
        />
      </PopoverAnchor>
      <PopoverContent shadow={'lg'} width={'2xs'}>
        <PopoverBody>
          <Tabs variant="panel-tabs" index={tabIndex} onChange={setTabIndex}>
            <TabList>
              <Tab fontSize={'sm'}>
                {t('project:viewer-interactions.calibration.visual', {
                  defaultValue: 'Visual',
                })}
              </Tab>
              <Tab fontSize={'sm'}>
                {t('project:viewer-interactions.calibration.numeric', {
                  defaultValue: 'Numeric',
                })}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel padding={0} marginTop={2}>
                <VStack align="stretch" spacing={1}>
                  {isComplete ? (
                    <form id="calibrate-page-form" onSubmit={onSubmit}>
                      <VStack align="stretch" spacing={1}>
                        <FormControl isRequired>
                          <FormLabel fontSize="xs">
                            {t(
                              'project:viewer-interactions.calibration.length'
                            )}
                          </FormLabel>
                          <Input
                            size={'xs'}
                            variant="filled"
                            borderRadius="md"
                            {...register('length', {
                              valueAsNumber: true,
                            })}
                            type="number"
                            step="any"
                            min="0"
                            autoFocus
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">
                            {t('project:viewer-interactions.calibration.unit')}
                          </FormLabel>
                          <Select
                            size={'xs'}
                            variant="filled"
                            {...register('units')}
                          >
                            <option value={CalibrationUnit.Mm}>
                              {t('millimeters')}
                            </option>
                            <option value={CalibrationUnit.M}>
                              {t('meters')}
                            </option>
                          </Select>
                        </FormControl>
                      </VStack>
                    </form>
                  ) : (
                    <Text fontSize={'sm'} color={'gray.500'}>
                      {t('project:viewer-interactions.calibration.prompt')}
                    </Text>
                  )}
                  {isComplete && (
                    <HStack
                      justifyContent={'flex-end'}
                      spacing={2}
                      marginTop={2}
                    >
                      {!shouldShowCalibration && (
                        <Button
                          size={'xs'}
                          variant="ghost"
                          colorScheme="gray"
                          onClick={toggleCalibration}
                        >
                          {t('project:viewer-interactions.calibration.cancel')}
                        </Button>
                      )}
                      <Button
                        size={'xs'}
                        colorScheme="brand"
                        variant="ghost"
                        type="submit"
                        form="calibrate-page-form"
                        isLoading={isUpdating}
                        isDisabled={!isComplete}
                      >
                        {t('project:viewer-interactions.calibrate')}
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </TabPanel>
              <TabPanel padding={0} marginTop={2}>
                <form id="calibrate-numeric-form" onSubmit={onSubmitNumeric}>
                  <VStack align="stretch" spacing={1}>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" color={'gray.500'}>
                        {t(
                          'project:viewer-interactions.calibration.scale-ratio',
                          { defaultValue: 'Scale Ratio (1:X)' }
                        )}
                      </FormLabel>
                      <Input
                        size={'xs'}
                        variant="filled"
                        borderRadius="md"
                        {...registerNumeric('scaleRatio', {
                          valueAsNumber: true,
                          min: 1,
                        })}
                        type="number"
                        step="any"
                        min="1"
                        placeholder="100"
                        autoFocus
                      />
                    </FormControl>
                    <HStack
                      justifyContent={'flex-end'}
                      spacing={2}
                      marginTop={2}
                    >
                      {!shouldShowCalibration && (
                        <Button
                          size={'xs'}
                          variant="ghost"
                          borderRadius="md"
                          colorScheme="gray"
                          onClick={toggleCalibration}
                        >
                          {t('project:viewer-interactions.calibration.cancel')}
                        </Button>
                      )}
                      <Button
                        size={'xs'}
                        colorScheme="brand"
                        variant="outline"
                        borderRadius="md"
                        type="submit"
                        isLoading={isNumericUpdating}
                        leftIcon={
                          <SparkelIcon icon={faCheck} color={'inherit'} />
                        }
                      >
                        {t('project:viewer-interactions.calibrate')}
                      </Button>
                    </HStack>
                  </VStack>
                </form>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
